<ui-section
  class="workflow-registry-page"
  layout="column"
  aria-label="Workflow registry workspace"
>
  <div *ngIf="isRegistryBrowseVisible() && canCreateWorkflow() && !registryAccessDenied() && (!isDraftCatalogVisible() || !draftFamilyCode())"
    class="workflow-registry-header-actions" role="group" aria-label="Workflow authoring">
    <button uiButton type="button" data-testid="create-process-from-registry" (click)="beginCreateWorkflow()">New workflow</button>
    <button uiButton type="button" [attr.aria-expanded]="templatesOpen()" aria-controls="workflow-registry-templates" (click)="templatesOpen.set(!templatesOpen())">Templates</button>
  </div>
  <ui-block width="fill">
    <ui-panel
      width="fill"
      class="workflow-registry-detail-panel"
      [class.workflow-registry-detail-panel--workbench]="
        isDraftWorkbenchOpen() || isVersionDetailVisible()
      "
      [panelTitle]="registryPanelTitle()"
      [title]="registryPanelTitle()"
      [subtitle]="registryPanelSubtitle()"
      data-testid="definition-detail-section"
    >
      <p *ngIf="visibilityMessage()" role="status" data-shared-visibility-message>{{ visibilityMessage() }}</p>
      <p
        *ngIf="publishedDraftPending() as version"
        role="status"
        data-testid="published-draft-handoff-pending"
      >
        Opening draft from published {{ version }}…
      </p>
      <ui-standard-state
        *ngIf="publishedDraftFailure() as failure"
        kind="error"
        [message]="publishedDraftFailureMessage(failure)"
        recoveryLabel="Retry opening draft"
        [recoveryDisabled]="isOpeningPublishedDraft || isLoadingDetail()"
        (recovery)="retryPublishedDraft(failure)"
        data-testid="published-draft-handoff-error"
      ></ui-standard-state>
      <ui-tab-group #registryTabs [navigationTabs]="isRegistryBrowseVisible() ? registryNavigationTabs : []"
        [activeTabId]="registryView()" navigationAriaLabel="Workflow registry views" [stretchTabs]="false"
        (tabRequested)="onRegistryTabRequested($event)" data-testid="registry-view-switcher">
      <section *ngIf="templatesOpen() && isRegistryBrowseVisible() && canCreateWorkflow()" id="workflow-registry-templates" aria-label="Workflow templates">
        <phms-hearing-enrollment-setup [templateOnly]="true" />
      </section>
      <div
        id="workflow-registry-versions-surface"
        class="workflow-registry-immutable-context"
        data-testid="immutable-definition-catalog"
        [hidden]="!isVersionCatalogVisible()"
      >
        <phms-platform-workflow-registry-catalog
          [page]="catalogPage()"
          [familyMode]="!catalogQuery().workflowCode"
          [familyPage]="familyPage()"
          [family]="selectedFamily()"
          [visibilityContext]="sharedVisibilityContext" [visibilityDetails]="visibilityDetails()" [visibilityStates]="visibilityViewStates()"
          (visibilityRequested)="requestFamilyVisibility($event)" (visibilityRefreshRequested)="refreshFamilyVisibility($event)" (visibilityRetryRequested)="retryFamilyVisibility($event)"
          (familySelected)="openFamily($event)"
          (allFamiliesRequested)="showAllFamilies()"
          (familyDraftsRequested)="openFamilyDrafts($event)"
          (familyArchivedDraftsRequested)="openFamilyDrafts($event, true)"
          [query]="catalogQuery()"
          [error]="catalogError()"
          [loading]="isLoadingList()"
          [canAuthor]="canCreateWorkflow()"
          [draftActionBlocked]="
            isLoadingList() || !!catalogError() || isLoadingDetail() || isOpeningPublishedDraft
          "
          [selectedDefinition]="selectedDefinitionKey()"
          (queryChange)="onCatalogQueryChange($event)"
          (retryRequested)="retryCatalog()"
          (definitionSelected)="selectDefinition($event)"
          (draftRequested)="onCatalogDraftRequested($event)"
        ></phms-platform-workflow-registry-catalog>
      </div>

      <ui-standard-state
        *ngIf="registryAccessDenied() && isDraftCatalogVisible()"
        kind="forbidden"
        message="You do not have permission to view workflow drafts. Contact an administrator if you need access. Your pending edits are retained."
        recoveryLabel="Check access"
        [recoveryDisabled]="isLoadingList()"
        (recovery)="retryCatalog()"
        data-testid="draft-registry-access-denied"
      ></ui-standard-state>
      <ng-template [workflowRegistryReadAccess]="!registryAccessDenied()">
        <div
          id="workflow-registry-drafts-surface"
          class="workflow-registry-draft-surface"
          data-testid="workflow-registry-drafts-surface"
          [hidden]="!isDraftCatalogVisible()"
        >
          <ng-container *ngIf="!isDraftWorkbenchOpen()">
            <button *ngIf="catalogQuery().workflowCode" uiButton type="button" data-return-family-history (click)="showRegistryView('versions')">Version history: {{ catalogQuery().workflowCode }}</button>
            <phms-workflow-family-visibility *ngIf="draftFamilyCode() as code" [workflowCode]="code" [context]="sharedVisibilityContext(code)" [detail]="familyVisibility(code)" [available]="familyVisibility(code)?.isAvailable === true"
              [canAuthor]="canCreateWorkflow()" [busy]="familyVisibilityState(code).busy" [blocked]="familyVisibilityState(code).blocked" [retry]="familyVisibilityState(code).retry" [message]="familyVisibilityState(code).message"
              (changeRequested)="requestFamilyVisibility($event)" (refreshRequested)="refreshFamilyVisibility(code)" (retryRequested)="retryFamilyVisibility(code)" />
          </ng-container>
          <phms-platform-workflow-draft-workspace
            [showAuthoringEntryButtons]="false"
            [entryPointsVisible]="isDraftCatalogVisible()"
            [selectedDefinition]="selectedDefinition()"
            (readAccessDenied)="onDraftReadAccessDenied()"
            (workbenchStateChange)="onDraftWorkbenchStateChange($event)"
            (definitionPublished)="onDefinitionPublished($event)"
            (editableDraftOpened)="onEditableDraftOpened($event)"
            (publishedVersionRequested)="openPublishedSource($event)"
          ></phms-platform-workflow-draft-workspace>
        </div>
      </ng-template>

      <div
        class="workflow-registry-immutable-context"
        data-testid="immutable-definition-detail"
        [hidden]="!isVersionDetailVisible()"
      >
        <button
          uiButton
          *ngIf="!selectedDefinition()"
          #detailLoadingBackButton
          type="button"
          class="workflow-registry-retry-button"
          data-testid="back-to-workflow-registry"
          (click)="backToRegistry()"
        >
          Back to workflow registry
        </button>

        <ui-standard-state
          *ngIf="isLoadingDetail()"
          kind="loading"
          message="Loading selected workflow definition."
          data-testid="detail-loading-state"
        ></ui-standard-state>

        <ng-container
          *ngIf="detailResult() as detail; else noSelectedDefinition"
        >
          <ui-standard-state
            *ngIf="detail.state !== 'resolved'"
            [kind]="detailReadState()"
            [message]="detailReadMessage()"
            [recoveryLabel]="
              detailReadState() === 'forbidden' ? undefined : 'Retry definition'
            "
            [recoveryDisabled]="isLoadingDetail()"
            (recovery)="retrySelectedDefinition()"
            data-testid="definition-detail-error"
          ></ui-standard-state>

          <ng-container *ngIf="selectedDefinition() as definition">
            <phms-platform-workflow-version-workspace
              #versionEditor
              [entryPointsVisible]="isVersionDetailVisible()"
              [definition]="definition"
              [activeView]="activeVersionView()"
              [newlyPublished]="isNewlyPublishedVersion()"
              [canTest]="canRunEligibilityTests()"
              [loading]="isLoadingDetail()"
              [interactionBlocked]="isOpeningPublishedDraft"
              [canEdit]="
                canCreateWorkflow() &&
                definition.definitionDocumentStatusCode === 'readable'
              "
              (editRequested)="onVersionDraftRequested(versionEditor, $event)"
              (activeViewChange)="onVersionViewChange($event)"
              (backRequested)="backToRegistry()"
              (lifecycleChanged)="onDefinitionLifecycleChanged()"
            ></phms-platform-workflow-version-workspace>

            <details class="workflow-registry-related-operations">
              <summary>
                <span>Related live operations</span>
                <small
                  >Runtime records, alert rules, and registry evidence</small
                >
              </summary>
              <div class="workflow-registry-related-operations__body">
                <p>
                  This information is related by workflow code or subject type.
                  It is live operational data and is not part of the immutable
                  version document above.
                </p>
                <section
                  class="workflow-registry-tabs"
                  aria-label="Related workflow operations"
                >
                  <ui-tab-group [tabConfig]="workflowTabs"></ui-tab-group>
                </section>
              </div>
            </details>
          </ng-container>
        </ng-container>

        <ng-template #noSelectedDefinition>
          <ui-standard-state
            *ngIf="!isLoadingDetail()"
            kind="empty"
            message="Select a workflow version from the registry to inspect it."
            data-testid="no-selected-definition"
          ></ui-standard-state>
        </ng-template>
      </div>
      </ui-tab-group>
    </ui-panel>
  </ui-block>
</ui-section>
